import React, { useEffect, useState } from 'react';
import { Button } from './Button';
import { CircleIcon, SquareIcon, HexagonIcon, CheckIcon, ZoomInIcon, ZoomOutIcon, RotateCwIcon, TextIcon, ImageIcon, Laptop, Coffee, Smartphone, Type } from 'lucide-react';
export const ProductPreview = () => {
  const [selectedShape, setSelectedShape] = useState('circle');
  const [selectedSize, setSelectedSize] = useState('medium');
  const [selectedColor, setSelectedColor] = useState('#00b8c4');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [previewMode, setPreviewMode] = useState('plain');
  const [customText, setCustomText] = useState('');
  const [isTextMode, setIsTextMode] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const colors = ['#00b8c4', '#ff6b6b', '#ffcc29', '#7d56f4', '#2ecc71', '#e74c3c', '#3498db', '#9b59b6'];
  const sizes = {
    small: {
      width: 100,
      height: 100
    },
    medium: {
      width: 150,
      height: 150
    },
    large: {
      width: 200,
      height: 200
    }
  };
  // Auto-rotate effect
  useEffect(() => {
    let rotationInterval;
    if (isAnimating) {
      rotationInterval = setInterval(() => {
        setRotation(prev => (prev + 5) % 360);
      }, 100);
    }
    return () => clearInterval(rotationInterval);
  }, [isAnimating]);
  const handleZoomIn = () => {
    if (zoomLevel < 1.5) {
      setZoomLevel(zoomLevel + 0.1);
    }
  };
  const handleZoomOut = () => {
    if (zoomLevel > 0.5) {
      setZoomLevel(zoomLevel - 0.1);
    }
  };
  const handleRotate = () => {
    setRotation((rotation + 45) % 360);
  };
  const toggleAnimation = () => {
    setIsAnimating(!isAnimating);
  };
  const renderStickerPreview = () => {
    const {
      width,
      height
    } = sizes[selectedSize];
    const scaledWidth = width * zoomLevel;
    const scaledHeight = height * zoomLevel;
    let stickerElement;
    if (selectedShape === 'circle') {
      stickerElement = <div className="rounded-full shadow-lg transition-all duration-300" style={{
        width: scaledWidth,
        height: scaledHeight,
        backgroundColor: selectedColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: `rotate(${rotation}deg)`
      }} data-id="element-171">
          <span className="text-white font-bold text-center px-2" style={{
          transform: `rotate(${-rotation}deg)`
        }} data-id="element-172">
            {isTextMode && customText ? customText : 'Your Sticker'}
          </span>
        </div>;
    } else if (selectedShape === 'square') {
      stickerElement = <div className="rounded-lg shadow-lg transition-all duration-300" style={{
        width: scaledWidth,
        height: scaledHeight,
        backgroundColor: selectedColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: `rotate(${rotation}deg)`
      }} data-id="element-173">
          <span className="text-white font-bold text-center px-2" style={{
          transform: `rotate(${-rotation}deg)`
        }} data-id="element-174">
            {isTextMode && customText ? customText : 'Your Sticker'}
          </span>
        </div>;
    } else {
      stickerElement = <div className="shadow-lg transition-all duration-300" style={{
        width: scaledWidth,
        height: scaledHeight,
        backgroundColor: selectedColor,
        clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: `rotate(${rotation}deg)`
      }} data-id="element-175">
          <span className="text-white font-bold text-center px-2" style={{
          transform: `rotate(${-rotation}deg)`
        }} data-id="element-176">
            {isTextMode && customText ? customText : 'Your Sticker'}
          </span>
        </div>;
    }
    // Different preview modes
    if (previewMode === 'plain') {
      return stickerElement;
    } else if (previewMode === 'laptop') {
      return <div className="relative" data-id="element-177">
          <div className="bg-gray-800 rounded-lg p-1 pt-8 shadow-xl" data-id="element-178">
            <div className="bg-gray-700 p-4 rounded-sm flex items-center justify-center" data-id="element-179">
              <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-600 rounded-full" data-id="element-180"></div>
              {stickerElement}
            </div>
          </div>
        </div>;
    } else if (previewMode === 'mug') {
      return <div className="relative" data-id="element-181">
          <div className="bg-white h-40 w-32 rounded-lg flex items-center justify-center shadow-xl relative" data-id="element-182">
            <div className="absolute right-0 top-1/2 transform translate-x-3 -translate-y-1/2 h-16 w-6 bg-white rounded-r-full border-r border-t border-b border-gray-300" data-id="element-183"></div>
            {stickerElement}
          </div>
        </div>;
    } else if (previewMode === 'phone') {
      return <div className="relative" data-id="element-184">
          <div className="bg-gray-900 h-64 w-32 rounded-xl p-2 shadow-xl flex items-center justify-center" data-id="element-185">
            <div className="bg-gray-800 h-full w-full rounded-lg flex items-center justify-center" data-id="element-186">
              {stickerElement}
            </div>
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 w-4 h-1 bg-gray-700 rounded-full" data-id="element-187"></div>
          </div>
        </div>;
    }
  };
  return <section id="customize" className="bg-white py-16 scroll-mt-20" data-id="element-188">
      <div className="container mx-auto px-4" data-id="element-189">
        <div className="text-center mb-12" data-id="element-190">
          <span className="text-[#ff6b6b] font-medium" data-id="element-191">
            Interactive Designer
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" data-id="element-192">
            Design Your Perfect Sticker
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-193">
            Customize every aspect of your sticker to match your vision. Choose
            from various shapes, sizes, and colors.
          </p>
        </div>
        <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16" data-id="element-194">
          {/* Preview */}
          <div className="flex flex-col items-center" data-id="element-195">
            <div className="flex items-center justify-center p-12 bg-gray-50 rounded-xl shadow-sm mb-4 relative overflow-hidden min-h-[300px] min-w-[300px]" data-id="element-196">
              <div className={`transform transition-all duration-300 ${isAnimating ? 'animate-pulse' : ''}`} data-id="element-197">
                {renderStickerPreview()}
              </div>
              {/* Preview Mode Selector */}
              <div className="absolute bottom-2 left-2 bg-white rounded-lg shadow-md p-2" data-id="element-198">
                <div className="flex space-x-2" data-id="element-199">
                  <button onClick={() => setPreviewMode('plain')} className={`p-1.5 hover:bg-gray-100 rounded-full transition-colors ${previewMode === 'plain' ? 'bg-gray-200' : ''}`} aria-label="Plain Preview" data-id="element-200">
                    <div className="h-5 w-5 rounded-full border-2 border-gray-700" data-id="element-201"></div>
                  </button>
                  <button onClick={() => setPreviewMode('laptop')} className={`p-1.5 hover:bg-gray-100 rounded-full transition-colors ${previewMode === 'laptop' ? 'bg-gray-200' : ''}`} aria-label="Laptop Preview" data-id="element-202">
                    <Laptop className="h-5 w-5 text-gray-700" data-id="element-203" />
                  </button>
                  <button onClick={() => setPreviewMode('mug')} className={`p-1.5 hover:bg-gray-100 rounded-full transition-colors ${previewMode === 'mug' ? 'bg-gray-200' : ''}`} aria-label="Mug Preview" data-id="element-204">
                    <Coffee className="h-5 w-5 text-gray-700" data-id="element-205" />
                  </button>
                  <button onClick={() => setPreviewMode('phone')} className={`p-1.5 hover:bg-gray-100 rounded-full transition-colors ${previewMode === 'phone' ? 'bg-gray-200' : ''}`} aria-label="Phone Preview" data-id="element-206">
                    <Smartphone className="h-5 w-5 text-gray-700" data-id="element-207" />
                  </button>
                </div>
              </div>
            </div>
            {/* Preview Controls */}
            <div className="flex space-x-3" data-id="element-208">
              <button onClick={handleZoomIn} className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" aria-label="Zoom In" data-id="element-209">
                <ZoomInIcon className="h-5 w-5 text-gray-700" data-id="element-210" />
              </button>
              <button onClick={handleZoomOut} className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" aria-label="Zoom Out" data-id="element-211">
                <ZoomOutIcon className="h-5 w-5 text-gray-700" data-id="element-212" />
              </button>
              <button onClick={handleRotate} className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" aria-label="Rotate" data-id="element-213">
                <RotateCwIcon className="h-5 w-5 text-gray-700" data-id="element-214" />
              </button>
              <button onClick={toggleAnimation} className={`p-2 ${isAnimating ? 'bg-[#ff6b6b] text-white' : 'bg-gray-100 text-gray-700'} rounded-full hover:bg-opacity-90 transition-colors`} aria-label="Toggle Animation" data-id="element-215">
                <Type className="h-5 w-5" data-id="element-216" />
              </button>
            </div>
          </div>
          {/* Controls */}
          <div className="w-full md:w-auto" data-id="element-217">
            <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-218">
              <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-219">
                Customize
              </h3>
              {/* Text Input */}
              <div className="mb-6" data-id="element-220">
                <label className="flex items-center justify-between text-gray-700 font-medium mb-2" data-id="element-221">
                  <span data-id="element-222">Custom Text</span>
                  <button onClick={() => setIsTextMode(!isTextMode)} className={`text-xs px-2 py-1 rounded ${isTextMode ? 'bg-[#00b8c4] text-white' : 'bg-gray-100'}`} data-id="element-223">
                    {isTextMode ? 'Enabled' : 'Disabled'}
                  </button>
                </label>
                <div className="relative" data-id="element-224">
                  <input type="text" value={customText} onChange={e => setCustomText(e.target.value)} placeholder="Enter sticker text" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00b8c4]" disabled={!isTextMode} data-id="element-225" />
                  <TextIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" data-id="element-226" />
                </div>
              </div>
              {/* Shape Selection */}
              <div className="mb-6" data-id="element-227">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-228">
                  Shape
                </label>
                <div className="flex space-x-4" data-id="element-229">
                  <button className={`p-3 rounded-md ${selectedShape === 'circle' ? 'bg-gray-100 ring-2 ring-[#00b8c4]' : 'bg-white border border-gray-200'}`} onClick={() => setSelectedShape('circle')} data-id="element-230">
                    <CircleIcon className="h-6 w-6 text-gray-700" data-id="element-231" />
                  </button>
                  <button className={`p-3 rounded-md ${selectedShape === 'square' ? 'bg-gray-100 ring-2 ring-[#00b8c4]' : 'bg-white border border-gray-200'}`} onClick={() => setSelectedShape('square')} data-id="element-232">
                    <SquareIcon className="h-6 w-6 text-gray-700" data-id="element-233" />
                  </button>
                  <button className={`p-3 rounded-md ${selectedShape === 'hexagon' ? 'bg-gray-100 ring-2 ring-[#00b8c4]' : 'bg-white border border-gray-200'}`} onClick={() => setSelectedShape('hexagon')} data-id="element-234">
                    <HexagonIcon className="h-6 w-6 text-gray-700" data-id="element-235" />
                  </button>
                </div>
              </div>
              {/* Size Selection */}
              <div className="mb-6" data-id="element-236">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-237">
                  Size
                </label>
                <div className="flex space-x-4" data-id="element-238">
                  {['small', 'medium', 'large'].map(size => <button key={size} className={`px-4 py-2 rounded-md capitalize ${selectedSize === size ? 'bg-gray-100 ring-2 ring-[#00b8c4] font-medium' : 'bg-white border border-gray-200'}`} onClick={() => setSelectedSize(size)} data-id="element-239">
                      {size}
                    </button>)}
                </div>
              </div>
              {/* Color Selection */}
              <div className="mb-8" data-id="element-240">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-241">
                  Color
                </label>
                <div className="flex flex-wrap gap-3" data-id="element-242">
                  {colors.map(color => <button key={color} className={`h-10 w-10 rounded-full flex items-center justify-center ${selectedColor === color ? 'ring-2 ring-offset-2 ring-gray-400' : ''} transform transition-transform hover:scale-110`} style={{
                  backgroundColor: color
                }} onClick={() => setSelectedColor(color)} data-id="element-243">
                      {selectedColor === color && <CheckIcon className="h-5 w-5 text-white" data-id="element-244" />}
                    </button>)}
                </div>
              </div>
              <div className="space-y-4" data-id="element-245">
                <Button size="large" className="w-full flex items-center justify-center gap-2" data-id="element-246">
                  <ImageIcon className="h-5 w-5" data-id="element-247" />
                  Upload Design
                </Button>
                <Button size="large" primary={false} className="w-full" data-id="element-248">
                  Continue to Checkout
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};